import { useState } from "react";
import { storyElement } from "@/lib/story-element";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Clapperboard, Activity, TrendingUp, TrendingDown, Clock,
  AlertTriangle, ChevronDown, ChevronRight, Zap, Eye,
  Globe, Users as UsersIcon, Target,
} from "lucide-react";

interface DirectorDebugPanelProps {
  state: any;
  variables: Record<string, any>;
}

const PHASE_COLORS: Record<string, string> = {
  exploration: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  tension: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  action: "bg-red-500/20 text-red-400 border-red-500/30",
  climax: "bg-purple-500/20 text-purple-400 border-purple-500/30",
  rest: "bg-sky-500/20 text-sky-400 border-sky-500/30",
};

export function DirectorDebugPanel({ state, variables }: DirectorDebugPanelProps) {
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    pacing: true,
    streaks: true,
    consequences: true,
    director: true,
    tone: false,
    world: true,
    factions: true,
    npcLog: false,
    audit: true,
    designIssues: true,
  });

  const toggle = (key: string) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  // Extract director-tracked state variables
  const pacingPhase = variables._pacing_phase || "exploration";
  const turnsInPhase = variables._turns_in_phase ?? 0;
  const turnsSinceEncounter = variables._turns_since_encounter ?? 0;
  const encounterCount = variables._encounter_count ?? 0;
  const consecutiveSuccesses = variables._consecutive_successes ?? 0;
  const consecutiveFailures = variables._consecutive_failures ?? 0;
  const pendingConsequences: any[] = variables._pending_consequences || [];
  const toneDrift = variables._tone_drift ?? 0;
  const worldEventsQueue: any[] = variables._world_events_queue || [];
  const npcOffscreenActions: any[] = variables._npc_offscreen_actions || [];
  const factionAgendas: any[] = variables._faction_agendas || [];
  const lastDirectorNotes = variables._last_director_notes;
  const turnCount = state?.turn_count ?? 0;
  const simTickCount = variables._sim_tick_count ?? 0;
  const lastSimTickTurn = variables._last_sim_tick_turn ?? 0;
  const plannedBeats: any[] = variables._director_plan || [];
  const lastAudit: any = variables._last_continuity_audit || null;
  const designIssues: any[] = (variables._design_issues || []).filter((i: any) => !i.resolved);
  const analyzerLog: any[] = variables._mechanic_analyzer_log || [];
  return (
    <ScrollArea {...storyElement("director-debug-panel")} className="h-full">
      <div className="p-3 space-y-2 overflow-hidden max-w-full">
        <div className="flex items-center gap-2 mb-1">
          <Clapperboard className="w-4 h-4 text-primary" />
          <span className="text-sm font-semibold text-foreground">Director Debug</span>
          <Badge variant="outline" className="text-[10px] h-4 ml-auto">
            Turn {turnCount}
          </Badge>
        </div>

        {/* Pacing */}
        <Section
          title="Pacing"
          icon={<Activity className="w-3.5 h-3.5" />}
          open={openSections.pacing}
          onToggle={() => toggle("pacing")}
        >
          <div className="space-y-2">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-xs text-muted-foreground">Phase:</span>
              <Badge variant="outline" className={`text-xs ${PHASE_COLORS[pacingPhase] || ""}`}>
                {pacingPhase}
              </Badge>
              <span className="text-[10px] text-muted-foreground ml-auto">
                {turnsInPhase} turn{turnsInPhase !== 1 ? "s" : ""} in phase
              </span>
            </div>
            <StatRow label="Turns since encounter" value={turnsSinceEncounter} warn={turnsSinceEncounter > 5} />
            <StatRow label="Total encounters" value={encounterCount} />
          </div>
        </Section>

        {/* Streaks */}
        <Section
          title="Difficulty Streaks"
          icon={<Zap className="w-3.5 h-3.5" />}
          open={openSections.streaks}
          onToggle={() => toggle("streaks")}
        >
          <div className="space-y-2">
            <div className="flex items-center gap-2 min-w-0 flex-wrap">
              <TrendingUp className="w-3 h-3 text-emerald-400" />
              <span className="text-xs text-muted-foreground">Consecutive successes:</span>
              <span className={`text-xs font-mono font-bold ${consecutiveSuccesses >= 3 ? "text-amber-400" : "text-foreground"}`}>
                {consecutiveSuccesses}
              </span>
              {consecutiveSuccesses >= 3 && (
                <Badge variant="outline" className="text-[10px] h-4 border-amber-500/30 text-amber-400">
                  Escalation
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2 min-w-0 flex-wrap">
              <TrendingDown className="w-3 h-3 text-red-400" />
              <span className="text-xs text-muted-foreground">Consecutive failures:</span>
              <span className={`text-xs font-mono font-bold ${consecutiveFailures >= 3 ? "text-sky-400" : "text-foreground"}`}>
                {consecutiveFailures}
              </span>
              {consecutiveFailures >= 3 && (
                <Badge variant="outline" className="text-[10px] h-4 border-sky-500/30 text-sky-400">
                  Mercy trigger
                </Badge>
              )}
            </div>
          </div>
        </Section>

        {/* Planned Beats */}
        {plannedBeats.length > 0 && (
          <Section
            title={`Planned Beats (${plannedBeats.length})`}
            icon={<Clapperboard className="w-3.5 h-3.5" />}
            open={openSections.director}
            onToggle={() => toggle("director")}
          >
            <div className="space-y-1.5">
              {plannedBeats.map((b: any, i: number) => {
                const triggerTurn = (b.created_turn || 0) + (b.turn_offset || 0);
                const turnsUntil = triggerTurn - turnCount;
                return (
                   <div key={i} className="rounded border border-border bg-background/50 p-2 text-xs space-y-1 overflow-hidden">
                     <div className="flex items-center gap-1.5 min-w-0">
                      <Badge variant="outline" className={`text-[10px] h-4 ${b.priority === "high" ? "border-amber-500/30 text-amber-400" : "border-border text-muted-foreground"}`}>
                        {b.priority || "normal"}
                      </Badge>
                      <span className="text-foreground truncate">{b.description}</span>
                    </div>
                    <div className="text-muted-foreground text-[10px]">
                      {turnsUntil <= 0 ? (
                        <Badge variant="destructive" className="text-[10px] h-4">READY</Badge>
                      ) : (
                        `triggers in ${turnsUntil} turn${turnsUntil !== 1 ? "s" : ""}`
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </Section>
        )}

        {/* Faction Agendas */}
        <Section
          title={`Faction Agendas (${factionAgendas.length})`}
          icon={<Target className="w-3.5 h-3.5" />}
          open={openSections.factions}
          onToggle={() => toggle("factions")}
        >
          {factionAgendas.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">No factions tracked (requires "living" world reactivity + faction entities)</p>
          ) : (
            <div className="space-y-2">
              {factionAgendas.map((fa: any, i: number) => (
                <div key={i} className="rounded border border-border bg-background/50 p-2 text-xs space-y-1.5 overflow-hidden">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-foreground">{fa.faction_name}</span>
                    <span className="text-[10px] text-muted-foreground">
                      tick T{fa.last_tick_turn || 0}
                    </span>
                  </div>
                  <div className="text-muted-foreground truncate" title={fa.current_goal}>
                    Goal: {fa.current_goal}
                  </div>
                  {/* Progress bar */}
                  <div className="space-y-0.5">
                    <div className="flex justify-between text-[10px]">
                      <span className="text-muted-foreground">Progress</span>
                      <span className={`font-mono ${(fa.progress || 0) >= 75 ? "text-amber-400 font-bold" : "text-foreground"}`}>
                        {fa.progress || 0}%
                      </span>
                    </div>
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          (fa.progress || 0) >= 75 ? "bg-amber-500" : "bg-primary/60"
                        }`}
                        style={{ width: `${Math.min(100, fa.progress || 0)}%` }}
                      />
                    </div>
                  </div>
                  {/* Recent action */}
                  {fa.actions_taken?.length > 0 && (
                    <div className="text-[10px] text-muted-foreground/70 italic truncate" title={fa.actions_taken[fa.actions_taken.length - 1]}>
                      Last: {fa.actions_taken[fa.actions_taken.length - 1]}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* NPC Offscreen Log */}
        <Section
          title={`NPC Offscreen (${npcOffscreenActions.length})`}
          icon={<UsersIcon className="w-3.5 h-3.5" />}
          open={openSections.npcLog}
          onToggle={() => toggle("npcLog")}
        >
          {npcOffscreenActions.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">No offscreen NPC activity yet</p>
          ) : (
            <div className="space-y-1">
              {[...npcOffscreenActions].reverse().slice(0, 10).map((a: any, i: number) => (
                <div key={i} className="text-xs border-l-2 border-primary/20 pl-2 py-0.5">
                  <div className="flex items-center gap-1">
                    <span className="font-medium text-foreground">{a.entity}</span>
                    <span className="text-[10px] text-muted-foreground ml-auto">T{a.turn}</span>
                  </div>
                  <div className="text-muted-foreground text-[11px]">{a.action}</div>
                  {a.motivation && (
                    <div className="text-[10px] text-muted-foreground/60 italic">{a.motivation}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Continuity Audit */}
        <Section
          title={`Continuity Audit${lastAudit ? ` (T${lastAudit.turn})` : ""}`}
          icon={<AlertTriangle className="w-3.5 h-3.5" />}
          open={openSections.audit}
          onToggle={() => toggle("audit")}
        >
          {!lastAudit ? (
            <p className="text-xs text-muted-foreground italic">No audit run yet</p>
          ) : (
            <div className="space-y-2">
              {/* Inconsistencies */}
              {(lastAudit.inconsistencies || []).length > 0 && (
                <div className="space-y-1">
                  <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Inconsistencies</span>
                  {lastAudit.inconsistencies.map((inc: any, i: number) => (
                    <div key={i} className="rounded border border-border bg-background/50 p-1.5 text-xs">
                      <div className="flex items-center gap-1">
                        <Badge variant="outline" className={`text-[9px] h-3.5 ${inc.severity === "high" ? "border-red-500/30 text-red-400" : "border-border text-muted-foreground"}`}>
                          {inc.type}
                        </Badge>
                        <span className="truncate text-foreground">{inc.description}</span>
                      </div>
                      {inc.fix && <p className="text-[10px] text-muted-foreground mt-0.5">Fix: {inc.fix}</p>}
                    </div>
                  ))}
                </div>
              )}
              {/* Plot holes */}
              {(lastAudit.plot_holes || []).length > 0 && (
                <div className="space-y-1">
                  <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Plot Holes</span>
                  {lastAudit.plot_holes.map((ph: any, i: number) => (
                    <div key={i} className="rounded border border-border bg-background/50 p-1.5 text-xs">
                      <span className="text-foreground">{ph.description}</span>
                      {ph.suggested_resolution && (
                        <p className="text-[10px] text-muted-foreground mt-0.5">→ {ph.suggested_resolution}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {/* Summary */}
              <div className="space-y-1 text-xs">
                <StatRow label="Rules injected" value={lastAudit.rules_injected || 0} />
                <StatRow label="Entities patched" value={lastAudit.entities_patched || 0} />
                {lastAudit.codex_notes && (
                  <p className="text-[10px] text-muted-foreground italic mt-1">{lastAudit.codex_notes}</p>
                )}
              </div>
              {(lastAudit.inconsistencies || []).length === 0 && (lastAudit.plot_holes || []).length === 0 && (
                <p className="text-xs text-emerald-400 italic">✓ No issues found</p>
              )}
            </div>
          )}
        </Section>

        {/* Consequence Queue */}
        <Section
          title={`Consequences (${pendingConsequences.length})`}
          icon={<Clock className="w-3.5 h-3.5" />}
          open={openSections.consequences}
          onToggle={() => toggle("consequences")}
        >
          {pendingConsequences.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">No pending consequences</p>
          ) : (
            <div className="space-y-1.5">
              {pendingConsequences.map((c: any, i: number) => {
                const turnsUntil = (c.trigger_turn ?? 0) - turnCount;
                return (
                   <div key={i} className="rounded border border-border bg-background/50 p-2 text-xs space-y-1 overflow-hidden">
                     <div className="flex items-center gap-1.5 min-w-0">
                      <AlertTriangle className="w-3 h-3 text-amber-400 shrink-0" />
                      <span className="text-foreground truncate">{c.description || "Unknown"}</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <span>Type: {c.type || "event"}</span>
                      <span className="ml-auto">
                        {turnsUntil <= 0 ? (
                          <Badge variant="destructive" className="text-[10px] h-4">NOW</Badge>
                        ) : (
                          `in ${turnsUntil} turn${turnsUntil !== 1 ? "s" : ""}`
                        )}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Section>

        {/* Design Issues */}
        {designIssues.length > 0 && (
          <Section
            title={`Design Issues (${designIssues.length})`}
            icon={<AlertTriangle className="w-3.5 h-3.5" />}
            open={openSections.designIssues}
            onToggle={() => toggle("designIssues")}
          >
            <div className="space-y-1.5">
              {designIssues.slice(0, 10).map((issue: any, i: number) => (
                 <div key={i} className="rounded border border-border bg-background/50 p-2 text-xs space-y-1 overflow-hidden">
                   <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                    <Badge variant="outline" className={`text-[10px] h-4 ${issue.severity === "critical" ? "border-red-500/30 text-red-400" : issue.severity === "warning" ? "border-amber-500/30 text-amber-400" : "border-border text-muted-foreground"}`}>
                      {issue.category}
                    </Badge>
                    <span className="text-foreground truncate">{issue.title}</span>
                  </div>
                  {issue.suggestion && (
                    <p className="text-[10px] text-muted-foreground">💡 {issue.suggestion}</p>
                  )}
                  <div className="text-[10px] text-muted-foreground/50">
                    T{issue.first_seen_turn} · ×{issue.occurrence_count}
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Tone & World */}
        <Section
          title="Tone & World Sim"
          icon={<Globe className="w-3.5 h-3.5" />}
          open={openSections.tone}
          onToggle={() => toggle("tone")}
        >
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Tone drift:</span>
              <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden relative">
                <div
                  className="absolute top-0 h-full bg-primary/60 rounded-full transition-all"
                  style={{
                    left: `${50 + Math.max(-50, Math.min(50, toneDrift * 50))}%`,
                    width: "4px",
                  }}
                />
                <div className="absolute top-0 left-1/2 h-full w-px bg-border" />
              </div>
              <span className="text-[10px] font-mono text-muted-foreground w-8 text-right">
                {toneDrift > 0 ? "+" : ""}{toneDrift.toFixed(1)}
              </span>
            </div>
            <StatRow label="World events queued" value={worldEventsQueue.length} />
            <StatRow label="Sim ticks run" value={simTickCount} />
            <StatRow label="Last sim tick" value={lastSimTickTurn > 0 ? `Turn ${lastSimTickTurn}` : "—"} />
          </div>
        </Section>

        {/* Last Director Notes */}
        <Section
          title="Last Director Notes"
          icon={<Eye className="w-3.5 h-3.5" />}
          open={openSections.director}
          onToggle={() => toggle("director")}
        >
          {lastDirectorNotes ? (
            <pre className="text-[11px] font-mono text-muted-foreground bg-background/50 rounded border border-border p-2 whitespace-pre-wrap break-words max-h-48 overflow-y-auto overflow-x-hidden">
              {typeof lastDirectorNotes === "string"
                ? lastDirectorNotes
                : JSON.stringify(lastDirectorNotes, null, 2)}
            </pre>
          ) : (
            <p className="text-xs text-muted-foreground italic">No director notes yet (send a message first)</p>
          )}
        </Section>

        {/* Butterfly Engine V2 — Narrative Threads */}
        {(() => {
          const seedsPlanted = variables._seeds_planted ?? 0;
          const seedsHatched = variables._seeds_hatched ?? 0;
          const butterflyLog: any[] = variables._butterfly_log || [];
          const deliveryQueue: any[] = variables._butterfly_delivery_queue || [];
          const plotConnections: any[] = variables._butterfly_plot_connections || [];
          const threadSummary: string = variables._butterfly_thread_summary || "";
          const storyProfile: string = variables._butterfly_story_profile || "";
          if (seedsPlanted === 0 && butterflyLog.length === 0 && deliveryQueue.length === 0) return null;
          return (
            <Section
              title={`🦋 Butterfly V2 (${seedsPlanted} planted, ${seedsHatched} hatched)`}
              icon={<Target className="w-3.5 h-3.5" />}
              open={openSections.butterfly ?? true}
              onToggle={() => toggle("butterfly")}
            >
              <div className="space-y-1.5">
                {/* Story Profile & Thread Summary */}
                {storyProfile && (
                  <div className="rounded border border-primary/20 bg-primary/5 p-1.5 text-[10px]">
                    <span className="text-primary font-semibold">Profile:</span>{" "}
                    <span className="text-muted-foreground">{storyProfile}</span>
                  </div>
                )}
                {threadSummary && (
                  <div className="rounded border border-purple-500/20 bg-purple-500/5 p-1.5 text-[10px]">
                    <span className="text-purple-400 font-semibold">Threads:</span>{" "}
                    <span className="text-muted-foreground">{threadSummary}</span>
                  </div>
                )}

                <div className="flex gap-3">
                  <StatRow label="Seeds planted" value={seedsPlanted} />
                  <StatRow label="Seeds hatched" value={seedsHatched} />
                </div>

                {/* Delivery Queue */}
                {deliveryQueue.length > 0 && (
                  <div className="space-y-1 mt-1">
                    <span className="text-[10px] text-amber-400 font-semibold">🎯 Pending Deliveries ({deliveryQueue.length}):</span>
                    {deliveryQueue.slice(0, 5).map((d: any, i: number) => (
                      <div key={i} className="rounded border border-amber-500/20 bg-amber-500/5 p-1.5 text-[10px] space-y-0.5 overflow-hidden">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <Badge variant="outline" className="text-[9px] px-1 py-0 h-4 bg-amber-500/20 text-amber-400 border-amber-500/30">
                            P{d.priority || 5}
                          </Badge>
                          <span className="text-muted-foreground truncate">{d.how_to_inject}</span>
                        </div>
                        {d.related_plot && <p className="text-[10px] text-muted-foreground/70 italic">→ Plot: {d.related_plot}</p>}
                      </div>
                    ))}
                  </div>
                )}

                {/* Plot Connections */}
                {plotConnections.length > 0 && (
                  <div className="space-y-1 mt-1">
                    <span className="text-[10px] text-purple-400 font-semibold">🔗 Plot Connections ({plotConnections.length}):</span>
                    {plotConnections.slice(-5).reverse().map((c: any, i: number) => (
                      <div key={i} className="flex items-center gap-1.5 text-[10px]">
                        <Badge variant="outline" className={`text-[9px] px-1 py-0 h-4 ${
                          c.connection_type === "subverts" ? "bg-red-500/20 text-red-400 border-red-500/30" :
                          c.connection_type === "accelerates" ? "bg-amber-500/20 text-amber-400 border-amber-500/30" :
                          "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                        }`}>
                          {c.connection_type}
                        </Badge>
                        <span className="text-muted-foreground truncate">seed→plot</span>
                      </div>
                    ))}
                  </div>
                )}

                {butterflyLog.length > 0 && (
                  <div className="space-y-1 mt-1">
                    <span className="text-[10px] text-muted-foreground font-semibold">Recent Activity:</span>
                    {butterflyLog.slice(-8).reverse().map((entry: any, i: number) => {
                      const actionLabel = entry.action === "hatched" ? "🌱 Hatched"
                        : entry.action === "accelerated" ? "⚡ Accelerated"
                        : entry.action === "evolved" ? "📈 Evolved"
                        : entry.action === "pruned" ? "✂️ Pruned"
                        : "🦋 Planted";
                      const actionColor = entry.action === "hatched" ? "bg-green-500/20 text-green-400 border-green-500/30"
                        : entry.action === "accelerated" ? "bg-amber-500/20 text-amber-400 border-amber-500/30"
                        : entry.action === "evolved" ? "bg-blue-500/20 text-blue-400 border-blue-500/30"
                        : entry.action === "pruned" ? "bg-red-500/20 text-red-400 border-red-500/30"
                        : "bg-purple-500/20 text-purple-400 border-purple-500/30";
                      return (
                        <div key={i} className="rounded border border-border bg-background/50 p-1.5 text-[10px] space-y-0.5 overflow-hidden">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <Badge variant="outline" className={`text-[9px] px-1 py-0 h-4 ${actionColor}`}>
                              {actionLabel}
                            </Badge>
                            <Badge variant="outline" className="text-[9px] px-1 py-0 h-4">{entry.type}</Badge>
                            {entry.new_phase && (
                              <Badge variant="outline" className="text-[9px] px-1 py-0 h-4 bg-blue-500/10 text-blue-400 border-blue-500/30">
                                →{entry.new_phase}
                              </Badge>
                            )}
                            {(entry.source === "butterfly_v2" || entry.source === "butterfly_agent") && (
                              <Badge variant="outline" className="text-[9px] px-1 py-0 h-4 bg-blue-500/10 text-blue-400 border-blue-500/30">agent</Badge>
                            )}
                            <span className="text-muted-foreground ml-auto">T{entry.turn}</span>
                          </div>
                          <p className="text-muted-foreground truncate">{entry.desc}</p>
                          {entry.outcome && <p className="text-green-400/80 truncate">→ {entry.outcome}</p>}
                          {entry.narrative_hook && <p className="text-cyan-400/70 text-[9px] truncate">🪝 {entry.narrative_hook}</p>}
                          {entry.related_plot_id && <p className="text-purple-400/70 text-[9px] truncate">📖 plot: {entry.related_plot_id}</p>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </Section>
          );
        })()}

        {/* Mechanic Analyzer Log */}
        {analyzerLog.length > 0 && (
          <Section
            title={`Mechanic Analyzers (${analyzerLog.length})`}
            icon={<Zap className="w-3.5 h-3.5" />}
            open={openSections.analyzers ?? false}
            onToggle={() => toggle("analyzers")}
          >
            <div className="space-y-1.5">
              {analyzerLog.slice(-10).reverse().map((entry: any, i: number) => (
                <div key={i} className="rounded border border-border bg-background/50 p-2 text-xs space-y-1 overflow-hidden">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Badge variant="outline" className="text-[9px] px-1 py-0 h-4">{entry.mechanic}</Badge>
                    <span className="text-[10px] text-muted-foreground">Turn {entry.turn}</span>
                  </div>
                  {entry.state_patches && Object.keys(entry.state_patches).length > 0 && (
                    <div className="text-[10px] text-green-400">
                      Patches: {JSON.stringify(entry.state_patches)}
                    </div>
                  )}
                  {entry.config_suggestions && Object.keys(entry.config_suggestions).length > 0 && (
                    <div className="text-[10px] text-amber-400">
                      Config: {JSON.stringify(entry.config_suggestions)}
                    </div>
                  )}
                  {entry.notes?.length > 0 && (
                    <div className="text-[10px] text-muted-foreground">{entry.notes.join("; ")}</div>
                  )}
                </div>
              ))}
            </div>
          </Section>
        )}
      </div>
    </ScrollArea>
  );
}

/* ─── Helpers ─── */

function Section({
  title, icon, open, onToggle, children,
}: {
  title: string; icon: React.ReactNode; open: boolean; onToggle: () => void; children: React.ReactNode;
}) {
  return (
    <Collapsible open={open} onOpenChange={onToggle}>
      <CollapsibleTrigger className="flex items-center gap-1.5 w-full text-left py-1 hover:text-foreground text-muted-foreground transition-colors">
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        {icon}
        <span className="text-xs font-medium">{title}</span>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="pl-5 pb-2 pt-1 overflow-hidden">{children}</div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function StatRow({ label, value, warn }: { label: string; value: number | string; warn?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={`text-xs font-mono font-bold ${warn ? "text-amber-400" : "text-foreground"}`}>
        {value}
      </span>
    </div>
  );
}
